import ProductTag from "./ProductTag";
import WeightRule from "./WeightRule";

export default class Product {
  id: number = 0;
  itemId: number = 0;
  branchId: number = 0;
  orderItemId: number = 0;
  name: string = "";
  image: string = "";
  images: string[] = [];
  hasMultipleImages: boolean = false;
  brandName: string = "";
  barcodes: Array<string> = [];
  subCategoryId: number = 0;
  subCategory: string = "";
  categoryId: number = 0;
  category: string = "";
  unitMeasureCode: string = "";
  unitMeasure: number = 0;
  unitMeasureDisplayOnApp: string = "";
  maxOrder: number = 0;
  price: number = 0;
  vat: number = 0;
  adjPrice: number = 0;
  adjPriceValue: number = 0;
  adjVATValue: number = 0;
  oldUnitPrice: number = 0;
  salesYear0: number = 0;
  salesYear1: number = 0;
  salesYear2: number = 0;
  salesYear3: number = 0;
  pointsPerc: number = 0;
  itemTypeId: number = 0;
  itemTypeName: string = "";
  hasAlternatives: boolean = false;
  isWeightSensitive: boolean = false;
  isTobacco: boolean = false;
  isAlcohol: boolean = false;
  promoType: number = 0;
  quantityInStock: number = 0;
  isOutOfStock: boolean = false;
  isPurchasedItemOutOfStock: boolean = false;
  isProbablyOOS: boolean = false;
  isPromoted: boolean = false;
  promoPercentile: number = 0;
  promoExpiryDate: Date | null = new Date();
  isFeatured: boolean = false;
  heroTag: string = "";
  isFavorite: boolean = false;
  exclFromMOV: boolean = false;
  hasProductInformation: boolean = false;
  normalizedUoM: string | null = null;
  pricePerNormalizedUoM: number | null = null;
  drsValue: number = 0;
  ebrdValue: number = 0;
  rrp: number = 0;
  adjRRPValue: number = 0;
  bundleTag: string | null = null;
  itemQuantityForBundle: number | null = null;
  tags: ProductTag[] | null = null;
  isEmployeeForceDiscarded: boolean = false;
  actualQtyOnHand: number = 0;
  isAlreadyCollected: boolean = false;
  alternativeItems: Array<any> = [];
  purchasedQuantity: number = 0;
  weightRule: WeightRule | null = null;
  attributes: { title: string, content: string }[] | null = null;

  /** Whether this item's quantity should be selected via a service weight/volume rule (predefined options + optional custom entry) rather than the normal unit stepper */
  static hasWeightRule(item: Product): boolean {
    return item.weightRule != null && item.isWeightSensitive == true;
  }

  /** Display suffix for a service quantity (e.g. "Kg" or "L"), driven by the item's own unit of measure */
  static getServiceQuantitySuffix(item: Product): string {
    return item.unitMeasureDisplayOnApp || item.unitMeasureCode || "";
  }

  /** PDP gallery images: the `images` array only when the API flagged this item as having multiple images, else the single `image` */
  static getDisplayImages(item: Product): string[] {
    if (item?.hasMultipleImages && item.images && item.images.length > 0) {
      return item.images;
    }

    return item?.image ? [item.image] : [];
  }

  static fetchItemObjectFromJson(product: any): Product {
    var item = {} as Product;

    item.id = product.id;
    item.itemId = product.itemId;
    item.branchId = product.branchId;
    item.name = product.name;
    item.image = product.image;
    item.images = product.images;
    item.hasMultipleImages = product.hasMultipleImages;
    item.brandName = product.brand;
    item.subCategoryId = product.subCategoryId;
    item.subCategory = product.subCategoryName;
    item.categoryId = product.categoryId;
    item.category = product.categoryName;
    item.unitMeasureCode = product.unitMeasureCode;
    item.unitMeasure = product.unitMeasureDescription;
    item.unitMeasureDisplayOnApp = product.unitMeasureDisplayOnApp;
    item.maxOrder = product.maxOrder;
    item.price = product.price;
    item.vat = product.vat;
    item.adjPrice = product.adjPriceValue;
    item.adjPriceValue = product.adjPriceValue;
    item.adjVATValue = product.adjVATValue;
    item.oldUnitPrice = product.oldAdjPrice;

        
    item.alternativeItems = product.alternativeItems != null ? product.alternativeItems.map((i: any) => this.fetchItemObjectFromJson(i)) : []
    item.itemTypeId = product.itemTypeId;
    item.itemTypeName = product.itemTypeName;
    item.isWeightSensitive = product.isVariableWeight;
    item.isTobacco = product.isTobacco;
    item.isAlcohol = product.isAlcohol;
    item.promoType = product.promoType;
    item.promoType = product.promoType;
    item.purchasedQuantity = product.quantity;
    item.isOutOfStock = product.isInventoryOOS;
    item.isPurchasedItemOutOfStock = product.isOutOfStock;
    item.isProbablyOOS = product.isInventoryProbablyOOS;
    item.isPromoted = product.isPromoted;
    item.promoPercentile = product.promoPercentile;
    item.promoExpiryDate = product.promoExpiryDate;
    product.expiryDate != null ? new Date(product.expiryDate) : null;
    item.isFeatured = product.isFeatured;
    item.isFavorite = product.isFavorite;
    item.exclFromMOV = product.exclFromMOV;
    item.hasProductInformation = product.hasProductInformation;
    item.normalizedUoM = product.normalizedUoM ?? null;
    item.pricePerNormalizedUoM = product.pricePerNormalizedUoM ?? null;
    item.drsValue = product.drsValue;
    item.ebrdValue = product.ebrdValue;
    item.rrp = product.rrp;
    item.adjRRPValue = product.adjRRPValue;
    item.bundleTag = product.bundleTag;
    item.itemQuantityForBundle = product.itemQuantityForBundle;
    item.isEmployeeForceDiscarded = product.isEmployeeForceDiscarded;
    item.actualQtyOnHand = product.actualQtyOnHand ?? 0;
    item.hasAlternatives = product.hasAlternatives;
    item.isAlreadyCollected = product.isAlreadyCollected;
    item.orderItemId = product.orderItemId;
    item.weightRule = product.weightRule != null ? WeightRule.fetchWeightRuleObjectFromJson(product.weightRule) : null;
    item.attributes = Array.isArray(product.attributes) ? product.attributes.map((attribute: any) => ({ title: attribute.title, content: attribute.content })) : null;
        if(typeof product.tags != "undefined" && product.tags != null && product.tags.length > 0) {
            item.tags = product.tags.map((tag: any) => ProductTag.fetchProductTagFromJson(tag)) 
        }

    return item;
  }

  static getItemsTypesFromItems(items: Product[]): Array<string> {
    let types: Array<string> = [];

    items.forEach((item: Product) => {
      if (!types.includes(item.itemTypeName)) types.push(item.itemTypeName);
    });

    return types;
  }

  static getItemsByTypeName(typeName: string, items: Product[]): Product[] {
    if (typeName == "All") return items;

    return items.filter((item: Product) => item.itemTypeName == typeName);
  }

  static getItemsByTypeMapping(
    items: Product[],
    typeName: string
  ): Map<string, Product[]> {
    var mapedItems: Map<string, Product[]> = new Map();

    if (typeName == "All") {
      for (let i = 0; i < items.length; i++) {
        mapedItems.set(
          items[i].itemTypeName,
          items.filter((x: any) => {
            if (x.itemTypeName == items[i].itemTypeName) return x;
          })
        );
      }
    } else {
      for (let i = 0; i < items.length; i++) {
        if (items[i].itemTypeName == typeName) {
          mapedItems.set(
            items[i].itemTypeName,
            items.filter((x: any) => {
              if (x.itemTypeName == items[i].itemTypeName) return x;
            })
          );
        }
      }
    }

    return mapedItems;
  }
}

export enum WeightedProductUnitMeasureEnum {
  mg,
  g,
  kg,
}

export class WeightedProductUnitMeasure {
  id: number = 0;
  weightedProductUnitMeasureEnum: WeightedProductUnitMeasureEnum =
    WeightedProductUnitMeasureEnum.g;
  displayString: string = "";

  /**
   *
   */
  constructor(
    id: number,
    weightedProductUnitMeasureEnum: WeightedProductUnitMeasureEnum,
    displayString: string
  ) {
    this.id = id;
    this.weightedProductUnitMeasureEnum = weightedProductUnitMeasureEnum;
    this.displayString = displayString;
  }

  static weightedProductUnitMeasureInMg = new WeightedProductUnitMeasure(
    0,
    WeightedProductUnitMeasureEnum.mg,
    "mg"
  );
  static weightedProductUnitMeasureInG = new WeightedProductUnitMeasure(
    1,
    WeightedProductUnitMeasureEnum.g,
    "g"
  );
  static weightedProductUnitMeasureInKg = new WeightedProductUnitMeasure(
    2,
    WeightedProductUnitMeasureEnum.kg,
    "kg"
  );

  static getWeightedProductUnitMeasureFromId(
    id: number
  ): WeightedProductUnitMeasure {
    switch (id) {
      case 0:
        return this.weightedProductUnitMeasureInMg;
      case 1:
        return this.weightedProductUnitMeasureInG;
      case 2:
        return this.weightedProductUnitMeasureInKg;
      default:
        return this.weightedProductUnitMeasureInG;
    }
  }

  static getWeightProductUnitMeasureFromString(
    unitMeasure: string
  ): WeightedProductUnitMeasure {
    if (unitMeasure == this.weightedProductUnitMeasureInMg.displayString)
      return this.weightedProductUnitMeasureInMg;
    else if (unitMeasure == this.weightedProductUnitMeasureInG.displayString)
      return this.weightedProductUnitMeasureInG;
    else if (unitMeasure == this.weightedProductUnitMeasureInKg.displayString)
      return this.weightedProductUnitMeasureInKg;
    else return this.weightedProductUnitMeasureInG;
  }

  static formattedWeight(weight: number, unitMeasureCode: string): string {
    return weight + unitMeasureCode;
  }

  static getTotalWeightValueInCorrectUnit(
    value: number,
    unitMeasureCode: string
  ): string {
    let unitMeasureId: number =
      this.getWeightProductUnitMeasureFromString(unitMeasureCode).id;

    while (value >= 1000) {
      value = value / 1000;
      unitMeasureId++;
    }

    return this.formattedWeight(
      value,
      this.getWeightedProductUnitMeasureFromId(unitMeasureId).displayString
    );
  }
}
